import { Action, createReducer, on } from '@ngrx/store';
import { userSignOutSuccess } from '../../authentication/store/authentication.action';
import {
  closeAllRows,
  getReferenceDataSuccess,
  openAllRows,
  openFirstLevel,
  setAccountingMode,
  toggleOpenedStatus,
} from './accounting.action';
import { Account, ACCOUNTING_MODE, AccountingMode, AccountNode, CompanyInfo } from '../model/accounting.model';

export default interface AccountingState {
  companyInfo: CompanyInfo | null;
  accounts: Account[];
  accountHierarchy: AccountNode | null;
  accountingMode: AccountingMode;
  accountNumberList: number[];
  openedAccounts: number[];
}

export const initialState: AccountingState = {
  companyInfo: null,
  accounts: [],
  accountHierarchy: null,
  accountingMode: ACCOUNTING_MODE.operating,
  accountNumberList: [],
  openedAccounts: [],
};

export const accountingReducer = createReducer(
  initialState,
  on(userSignOutSuccess, (): AccountingState => initialState),
  on(getReferenceDataSuccess, (state, { companyData }): AccountingState => {
    return {
      ...state,
      companyInfo: companyData.info,
      accounts: companyData.accounts,
      accountHierarchy: companyData.accountHierarchy,
      accountNumberList: companyData.accountNumberList,
    };
  }),
  on(toggleOpenedStatus, (state, { accountNo }): AccountingState => {
    let updatedOpenedAccounts: number[] = [];
    const opened = state.openedAccounts.includes(accountNo);
    if (opened) {
      updatedOpenedAccounts = state.openedAccounts.filter(no => accountNo !== no);
    } else {
      updatedOpenedAccounts = [...state.openedAccounts, ...getChildrenAccount(accountNo, getAllMainAccountGroups(state.accountHierarchy))];
    }
    return {
      ...state, openedAccounts: updatedOpenedAccounts
    }
  }),
  // TODO ? on(displayIntermediaryAccountGroupsChanged, (state, { enabled }): AccountingState => {
  //   if (!enabled) {
  //     const openedMainAccountGroups = state.openedAccounts.filter(accountNo => accountNo < 10);
  //     return ({
  //       ...state, openedAccounts: state.accountNumberList.filter(accountNo => openedMainAccountGroups.includes(+String(accountNo)[0]))
  //     });
  //   }
  //   return state;
  // }),
  on(openAllRows, (state): AccountingState => ({
    ...state, openedAccounts: getAllMainAccountGroups(state.accountHierarchy).map(getIntermediaryAccountNumberList).flat()
  })),
  on(openFirstLevel, (state): AccountingState => ({
    ...state, openedAccounts: getAllMainAccountGroups(state.accountHierarchy).map(accountNode => accountNode.info.number)
  })),
  on(closeAllRows, (state): AccountingState => ({
    ...state, openedAccounts: []
  })),
  on(setAccountingMode, (state, { mode }): AccountingState => ({
    ...state, accountingMode: mode
  })),
);

const getAllMainAccountGroups = (accountNode: AccountNode | null): AccountNode[] => {
  if (!accountNode) {
    return [];
  }
  return accountNode.children.map(child => child.children).flat()
}

const getChildrenAccount = (accountNo: number, accountNodes: AccountNode[]): number[] => {
  const child = accountNodes.filter(node => String(accountNo).startsWith(String(node.info.number)))[0];
  if (child.info.number === accountNo) {
    return getChildrenAccountNumber(child);
  }
  return getChildrenAccount(accountNo, child.children);
}

const getChildrenAccountNumber = (accountNode: AccountNode): number[] => {
  if (accountNode.children.length === 0) {
    return [];
  }
  return [ accountNode.info.number, accountNode.children.map(childNode => getChildrenAccountNumber(childNode)).flat() ].flat();
}

const getIntermediaryAccountNumberList = (accountNode: AccountNode): number[] => {
  if (accountNode.children.length === 0 || accountNode.info.number > 1000) {
    return [];
  }
  return [ accountNode.info.number, accountNode.children.map(childNode => getChildrenAccountNumber(childNode)).flat() ].flat();
}

export const reducer = (state: AccountingState | undefined, action: Action): AccountingState => accountingReducer(state, action);
